'use client';

import type { ComponentType, PointerEvent, ReactNode } from 'react';

import '~/shared/components/auth-layout/auth-layout.scss';
import '~/shared/styles/native-scrollbars.scss';
import '~/shared/styles/text-selection.scss';

import ThemedLogo from '~/shared/components/themed-logo';
import { useTypedTranslation } from '~/shared/hooks';

type AuthLayoutPropsType = Readonly<{
  isRedirection?: boolean;
  title?: ReactNode;
  actions?: ReactNode;
  form?: ReactNode;
  topContent?: ReactNode;
  app?: string;
  Content?: ComponentType<{ t: ReturnType<typeof useTypedTranslation> }>;
  // Kept for backward compatibility with existing callers. AuthLayout uses its own typed translator.
  t?: unknown;
}>;

const AuthLayout = ({
  title,
  isRedirection = false,
  actions,
  app = 'platform',
  form,
  topContent,
  Content,
}: AuthLayoutPropsType) => {
  const t = useTypedTranslation(['text', 'button']);

  const handleParallaxMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    const style = event.currentTarget.style;

    style.setProperty('--auth-content-x', `${x * -16}px`);
    style.setProperty('--auth-content-y', `${y * -12}px`);
    style.setProperty('--auth-orb-x', `${x * 28}px`);
    style.setProperty('--auth-orb-y', `${y * 22}px`);
  };

  const resetParallax = (event: PointerEvent<HTMLDivElement>) => {
    const style = event.currentTarget.style;
    style.setProperty('--auth-content-x', '0px');
    style.setProperty('--auth-content-y', '0px');
    style.setProperty('--auth-orb-x', '0px');
    style.setProperty('--auth-orb-y', '0px');
  };

  return (
    <div className="auth-layout">
      <div className="auth-layout__background" onPointerMove={handleParallaxMove} onPointerLeave={resetParallax}>
        <div className="auth-layout__background-image">
          <span className="auth-layout__orb auth-layout__orb--one" aria-hidden="true" />
          <span className="auth-layout__orb auth-layout__orb--two" aria-hidden="true" />
          <span className="auth-layout__orb auth-layout__orb--three" aria-hidden="true" />
          <span className="auth-layout__orb auth-layout__orb--four" aria-hidden="true" />
          <span className="auth-layout__orb auth-layout__orb--ring" aria-hidden="true" />
          <div className="auth-layout__parallax-content">
            <div className="auth-layout__brand-shell">
              <ThemedLogo className="auth-layout__brand-logo" priority />
            </div>
            <div className="auth-layout__app-title">{t(app === 'platform' ? 'text.platform' : 'text.partner-program')}</div>
          </div>
        </div>
      </div>

      <div className="auth-layout__form-block">
        <div className="auth-layout__block">
          {topContent}
          {title ? <h1 className="auth-layout__title">{title}</h1> : null}
          {isRedirection ? (
            <h1>{t('text.redirection')}</h1>
          ) : Content ? (
            <Content t={t} />
          ) : (
            <section className="auth-layout__form">{form}</section>
          )}
        </div>
        <section className="auth-layout__actions">{actions}</section>
      </div>
    </div>
  );
};

export default AuthLayout;
